import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import ArticleCard from '@/components/base/ArticleCard';
import { articles, categories } from '@/mocks/articles';
import { siteInfo } from '@/mocks/site';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);

  if (!category) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <i className="ri-error-warning-line text-5xl text-foreground-300 mb-4"></i>
            <h1 className="text-xl font-semibold text-foreground-950 mb-2">未找到该分类</h1>
            <Link to="/" className="text-sm text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap">
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const categoryArticles = articles.filter((a) => a.categorySlug === slug);

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: siteInfo.url },
      { '@type': 'ListItem', position: 2, name: category.name, item: `${siteInfo.url}/category/${slug}` },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={`${category.name} - ${siteInfo.name}`}
        description={`浏览${category.name}相关的所有文章。${category.description}`}
        keywords={`${category.name},${siteInfo.keywords}`}
        canonicalUrl={`${siteInfo.url}/category/${slug}`}
        ogType="website"
      />

      <main>
        <Navbar />

        <section className="pt-20 pb-10 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: category.name },
              ]}
            />

            <div className="mt-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100">
                  <i className={`${category.icon} text-lg text-primary-500`}></i>
                </div>
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-950">{category.name}</h1>
              </div>
              <p className="text-sm md:text-base text-foreground-600 max-w-2xl">
                {category.description}
              </p>
              <p className="text-xs text-foreground-400 mt-2">
                共 {categoryArticles.length} 篇文章
              </p>
            </div>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
          {/* Categories tabs */}
          <div className="mb-8 overflow-x-auto">
            <div className="flex items-center gap-2 min-w-max pb-2">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    cat.slug === slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-50 text-foreground-600 border border-background-200/70 hover:border-background-300/60'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>

          {categoryArticles.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {categoryArticles.map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <i className="ri-file-list-2-line text-5xl text-foreground-300 mb-4"></i>
              <p className="text-foreground-500">该分类下暂无文章</p>
            </div>
          )}
        </section>

        <Footer />
      </main>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
    </>
  );
}